// Single source of truth for the locale JSONs and the supported-language
// list. apps/mobile and apps/web-rnw consume `getResources()` rather than
// importing the JSON files directly — adding or removing a locale is a
// one-line change here.

import en from "./locales/en.json";
import it from "./locales/it.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import ru from "./locales/ru.json";

export const supportedLngs = ["en", "it", "fr", "de", "ru"] as const;

export type SupportedLng = (typeof supportedLngs)[number];

export const fallbackLng: SupportedLng = "en";

// Maps a raw language tag ("fr-CH", "DE", null) to a supported base language,
// or null when it is not one of ours. The one normaliser: the mobile locale
// resolver and the web screens read the active language through it.
export function normaliseLng(raw: string | null | undefined): SupportedLng | null {
  if (!raw) return null;
  const base = raw.split("-")[0]?.toLowerCase() ?? "";
  return (supportedLngs as readonly string[]).includes(base)
    ? (base as SupportedLng)
    : null;
}

// Same, but never null: an unsupported tag reads as the fallback ("en").
export function toSupportedLng(raw: string | null | undefined): SupportedLng {
  return normaliseLng(raw) ?? fallbackLng;
}

// Language names in their OWN language (endonyms). A user who has landed
// on a UI they can't read must still be able to find their way out, so
// these are deliberately never translated.
//
// Hoisted here on 2026-08-22 (PE-21 slice B) because the same five
// strings already existed in three places — mobile's LanguagePickerModal,
// web-rnw's PreferencesScreen, and the legacy web switcher — and the
// program editor's language chip would have been the fourth.
export const LANGUAGE_ENDONYMS: Record<SupportedLng, string> = {
  en: "English",
  it: "Italiano",
  fr: "Français",
  de: "Deutsch",
  ru: "Русский",
};

// Return shape is structurally compatible with i18next's `Resource` type,
// without taking a runtime dep on i18next here. Both apps cast via init().
type Bundle = Record<SupportedLng, { translation: Record<string, unknown> }>;

export function getResources(): Bundle {
  return {
    en: { translation: en as Record<string, unknown> },
    it: { translation: it as Record<string, unknown> },
    fr: { translation: fr as Record<string, unknown> },
    de: { translation: de as Record<string, unknown> },
    ru: { translation: ru as Record<string, unknown> },
  };
}
